'use client';

import { OutputConfig } from '@/services/termSheet/types/common';
import { AnimatePresence, motion } from 'framer-motion';
import styles from './termSheetPreview.module.scss';
import { formatDate } from '@/services/date';
import TermSheetPreviewGroup from '@/app/tools/term-sheet/components/TermSheetPreview/components/TermSheetPreviewGroup';
import { slideInOut } from '@baseline/ui';
import { DISCLOSURE_DEFAULT_VALUE } from '@/services/termSheet/services';

const BLANK_CONFIG: OutputConfig = {
  borrower: '',
  guarantor: '',
  address: '',
  groups: {
    terms: {
      title: 'TERMS',
      fields: [
        { label: 'Total Loan Amount', value: '' },
        { label: 'Interest Rate', value: '' },
        { label: 'Monthly Payment', value: '' },
        { label: 'Purpose', value: '' },
        { label: 'Closing Date', value: '' },
        { label: 'Loan to Cost', value: '' }
      ]
    },
    additionalDetails: {
      title: 'ADDITIONAL DETAILS',
      fields: [
        { label: 'Loan Term', value: '' },
        { label: 'Rate Type', value: '' },
        { label: 'Amortization Type', value: '' },
        { label: 'Lien', value: '' },
        { label: 'Prepayment Penalty', value: '' },
        { label: 'Property Type', value: '' },
        { label: 'Occupancy', value: '' }
      ]
    },
    cashRequiredAtClosing: {
      title: 'CASH TO CLOSE',
      fields: [
        { label: 'Down Payment', value: '' },
        { label: 'Third Party Costs', value: '' },
        {
          label: 'Total Cash Required',
          value: '',
          bold: true,
          topDivider: true
        }
      ]
    }
  },
  disclosure: null
};

function FieldValue({
  value,
  className
}: {
  value?: string;
  className?: string;
}) {
  return (
    <AnimatePresence initial={false} mode="popLayout">
      {value ? (
        <motion.div
          {...slideInOut()}
          key="value"
          className={
            className
              ? `${styles.headingFieldValue} ${className}`
              : styles.headingFieldValue
          }
        >
          {value}
        </motion.div>
      ) : (
        <motion.div
          {...slideInOut()}
          key="skeleton"
          className={styles.headerBlankValue}
        />
      )}
    </AnimatePresence>
  );
}

function HeadingField({
  label,
  value,
  valueClassName,
  className
}: {
  label: string;
  value?: string;
  valueClassName?: string;
  className?: string;
}) {
  return (
    <div
      className={
        className ? `${styles.headingField} ${className}` : styles.headingField
      }
    >
      <div className={styles.headingFieldLabel}>{label}</div>
      <FieldValue value={value} className={valueClassName} />
    </div>
  );
}

export default function TermSheetPreview({
  data = BLANK_CONFIG
}: {
  data?: OutputConfig;
}) {
  const formattedDate = formatDate(new Date());

  return (
    <div className={styles.termSheetPreview}>
      <div className={styles.header}>
        <div className={styles.left}>
          <h2 className={styles.title}>Term Sheet</h2>
          <div>
            <HeadingField label="COLLATERAL ADDRESS" value={data?.address} />
            <div className={styles.headingRow}>
              <HeadingField label="BORROWER" value={data?.borrower} />
              <HeadingField label="GUARANTOR" value={data?.guarantor} />
              <HeadingField
                label="DATE"
                value={formattedDate}
                valueClassName={styles.headingFieldValueNoWrap}
                className={styles.headingFieldDate}
              />
            </div>
          </div>
        </div>
        <div className={styles.right}>
          <AnimatePresence initial={false} mode="popLayout">
            <motion.div
              key={data?.logoUrl ? 'logo' : 'placeholder'}
              {...slideInOut(50)}
            >
              {data?.logoUrl ? (
                <img
                  src={data.logoUrl}
                  alt="Logo"
                  className={styles.logoImage}
                />
              ) : (
                <div className={styles.logo}>LOGO</div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
      {Object.entries(data?.groups || {}).map(([key, group]) => (
        <TermSheetPreviewGroup
          key={key}
          title={group.title}
          fields={group.fields}
        />
      ))}
      <div className={styles.disclosure}>
        {data.disclosure ?? DISCLOSURE_DEFAULT_VALUE}
      </div>
    </div>
  );
}
